import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, FormControlLabel, IconButton, Switch, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { DoneAllRounded, RedoRounded, UndoRounded } from '@mui/icons-material';
import DeckCanvas, { OUTLINE_TONES } from './DeckCanvas';
import DeckFilmstrip from './DeckFilmstrip';
import DeckPanel from './DeckPanel';
import ShapeInspector from './ShapeInspector';
import SlideInspector from './SlideInspector';
import {
  acceptAllSuggestions,
  acceptFields,
  deckCounts,
  dropSlide,
  fieldsByShape as indexFieldsByShape,
  rejectFields,
  restoreSlide,
  setShapeAction,
  setShapeVariable,
  shapesBySlide as indexShapesBySlide,
  slideSizeOf,
  slidesOf,
  suggestedFieldIds,
  updateBrief,
  updateField,
  updateSlide,
} from './deckModel';

/** Screen-reader-only text. */
const VISUALLY_HIDDEN = { position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

/**
 * Whether a key event comes from a text input (native undo applies there).
 * @param {EventTarget|null} target - Event target.
 * @returns {boolean} True for inputs, textareas and editable content.
 */
function isTextTarget(target) {
  if (!target || typeof target !== 'object') return false;
  const tag = String(target.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable === true;
}

/**
 * The deck template workspace: filmstrip, slide canvas with outlines and the shape, slide and deck
 * inspectors. Holds no data access: the page (or the dev harness) passes the editor state from
 * useDeckHistory and the slide image URLs.
 * @param {object} props - Props.
 * @param {ReturnType<import('./deckHistory').useDeckHistory>} props.editor - Model with undo.
 * @param {Object<string, {png: string|null, thumb: string|null}>} props.images - Image URLs by slide key.
 * @param {boolean} [props.readOnly] - No edits (an import still running, a failed load).
 * @param {string} [props.imageNote] - Canvas text when a slide has no image.
 * @param {function(): void} [props.onImageError] - A slide image failed to load.
 * @param {function({slideKey: string|null, shapeKey: string|null, slideNumber: number|null}): void} [props.onFocusChange] -
 *   The selected slide or box changed (the page tells Inky what is in focus).
 * @returns {React.ReactElement}
 */
function DeckWorkspace({ editor, images, readOnly = false, imageNote = '', onImageError, onFocusChange }) {
  const { model, apply, undo, redo, canUndo, canRedo } = editor;
  const slides = useMemo(() => slidesOf(model), [model]);
  const shapesBySlide = useMemo(() => indexShapesBySlide(model), [model]);
  const fieldsByShape = useMemo(() => indexFieldsByShape(model), [model]);
  const slideSize = useMemo(() => slideSizeOf(model), [model]);
  const counts = useMemo(() => deckCounts(model), [model]);
  const suggestionCounts = useMemo(() => {
    const out = {};
    slides.forEach((slide) => {
      out[slide.key] = suggestedFieldIds(model, slide.key).length;
    });
    return out;
  }, [model, slides]);

  const [selectedSlideKey, setSelectedSlideKey] = useState(() => slides[0]?.key || null);
  const [selectedShapeKey, setSelectedShapeKey] = useState(null);
  const [tab, setTab] = useState('slide');
  const [showOutlines, setShowOutlines] = useState(true);
  const [announcement, setAnnouncement] = useState('');
  /** Fields removed by an action change, so switching back keeps the prompt (not saved). */
  const parkedFieldsRef = useRef(new Map());
  const shapeHeadingRef = useRef(null);
  const slideHeadingRef = useRef(null);
  /**
   * Move focus on purpose after an action removes the focused control (an accepted suggestion's
   * buttons, a restored slide's alert, a box picked from the Slide tab), so keyboard and screen
   * reader users keep their place instead of landing on the page body.
   * @param {React.RefObject} ref - Where focus goes (read after React has committed).
   */
  const focusSoon = useCallback((ref) => {
    window.setTimeout(() => ref.current?.focus?.(), 0);
  }, []);

  const slide = slides.find((s) => s.key === selectedSlideKey) || slides[0] || null;
  const slideShapes = slide ? shapesBySlide.get(slide.key) || [] : [];
  const shapeIndex = slideShapes.findIndex((s) => s.key === selectedShapeKey);
  const shape = shapeIndex >= 0 ? slideShapes[shapeIndex] : null;

  // Keep a valid slide selected when slides change (a reset or reload).
  useEffect(() => {
    if (slides.length && !slides.some((s) => s.key === selectedSlideKey)) setSelectedSlideKey(slides[0].key);
  }, [slides, selectedSlideKey]);

  const focusSlideKey = slide?.key || null;
  const focusShapeKey = shape?.key || null;
  const focusSlideNumber = slide ? slides.indexOf(slide) + 1 : null;
  useEffect(() => {
    if (typeof onFocusChange === 'function') onFocusChange({ slideKey: focusSlideKey, shapeKey: focusShapeKey, slideNumber: focusSlideNumber });
  }, [onFocusChange, focusSlideKey, focusShapeKey, focusSlideNumber]);

  const announce = useCallback((text) => setAnnouncement(text), []);

  const selectSlide = useCallback((key) => {
    setSelectedSlideKey(key);
    setSelectedShapeKey(null);
    setTab((current) => (current === 'shape' ? 'slide' : current));
  }, []);

  const selectShape = useCallback((key) => {
    setSelectedShapeKey(key);
    if (key) setTab('shape');
  }, []);

  const handleUndo = useCallback(() => {
    if (!canUndo) return;
    undo();
    announce('Undone.');
  }, [canUndo, undo, announce]);
  const handleRedo = useCallback(() => {
    if (!canRedo) return;
    redo();
    announce('Redone.');
  }, [canRedo, redo, announce]);

  // Undo and redo outside text inputs (inputs keep their own native undo).
  useEffect(() => {
    if (readOnly) return undefined;
    const onKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || isTextTarget(event.target)) return;
      const key = String(event.key).toLowerCase();
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        handleUndo();
      } else if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault();
        handleRedo();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [readOnly, handleUndo, handleRedo]);

  const changeAction = (action) => {
    if (!shape) return;
    const current = fieldsByShape.get(shape.key);
    if (current) parkedFieldsRef.current.set(shape.key, current);
    apply((m) => setShapeAction(m, shape.key, action, { previousField: parkedFieldsRef.current.get(shape.key) }));
  };

  const acceptAll = () => {
    const n = counts.suggested;
    if (readOnly || n === 0) return;
    apply(acceptAllSuggestions);
    announce(`Accepted ${n} suggestion${n === 1 ? '' : 's'}.`);
  };
  // With nothing left to accept the button stays focusable (aria-disabled), so focus is not lost.
  const nothingToAccept = !readOnly && counts.suggested === 0;

  const ratio = `${slideSize.cx} / ${slideSize.cy}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }} data-testid="deck-workspace">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Undo (Ctrl+Z)">
          <span>
            <IconButton aria-label="Undo" onClick={handleUndo} disabled={readOnly || !canUndo} size="small">
              <UndoRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Redo (Ctrl+Shift+Z)">
          <span>
            <IconButton aria-label="Redo" onClick={handleRedo} disabled={readOnly || !canRedo} size="small">
              <RedoRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          size="small"
          variant={counts.suggested ? 'contained' : 'outlined'}
          startIcon={<DoneAllRounded />}
          disabled={readOnly}
          aria-disabled={nothingToAccept || undefined}
          data-testid="deck-accept-all"
          onClick={acceptAll}
          sx={{ borderRadius: 999, ...(nothingToAccept ? { color: 'text.disabled', borderColor: 'action.disabledBackground', cursor: 'default', '&:hover': { bgcolor: 'transparent', borderColor: 'action.disabledBackground' } } : {}) }}
        >
          Accept all suggestions{counts.suggested ? ` (${counts.suggested})` : ''}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Box component="ul" aria-label="Outline colours" sx={{ listStyle: 'none', m: 0, p: 0, display: { xs: 'none', sm: 'flex' }, flexWrap: 'wrap', gap: 1.5 }}>
          {['ai', 'suggested', 'variable', 'fixed'].map((tone) => {
            const { Icon } = OUTLINE_TONES[tone];
            return (
              <Box component="li" key={tone} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12, color: 'text.secondary' }}>
                <Box aria-hidden="true" sx={{ width: 14, height: 10, border: `2px ${OUTLINE_TONES[tone].style} ${OUTLINE_TONES[tone].color}`, borderRadius: '2px' }} />
                <Icon aria-hidden="true" sx={{ fontSize: 12 }} />
                {OUTLINE_TONES[tone].label}
              </Box>
            );
          })}
        </Box>
        <FormControlLabel
          control={<Switch size="small" checked={!showOutlines} onChange={(event) => setShowOutlines(!event.target.checked)} />}
          label={<Typography sx={{ fontSize: 13 }}>As is</Typography>}
          sx={{ mr: 0, ml: 1 }}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '180px minmax(0, 1fr) 340px' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 'var(--q-radius-card)', minWidth: 0, order: { xs: 1, md: 0 } }}>
          <DeckFilmstrip
            slides={slides}
            images={images}
            selectedKey={slide?.key || null}
            onSelect={selectSlide}
            suggestionCounts={suggestionCounts}
            ratio={ratio}
          />
        </Box>

        <Box sx={{ minWidth: 0, order: { xs: 0, md: 1 } }}>
          {slide ? (
            <DeckCanvas
              slide={slide}
              shapes={slideShapes}
              fieldsByShape={fieldsByShape}
              slideSize={slideSize}
              imageUrl={images[slide.key]?.png || null}
              selectedShapeKey={shape?.key || null}
              onSelectShape={selectShape}
              showOutlines={showOutlines}
              imageNote={slide.hidden ? 'This slide is hidden in PowerPoint, so it was not rendered.' : imageNote}
              onImageError={onImageError}
            />
          ) : (
            <Typography color="text.secondary">This template has no slides.</Typography>
          )}
          {slide && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
              Slide {slide.number} of {slides.length}. Select a box to choose what Quill does with it. Boxes are moved and resized in PowerPoint.
            </Typography>
          )}
        </Box>

        <Box
          component="aside"
          aria-label="Inspector"
          sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 'var(--q-radius-card)', minWidth: 0, order: 2 }}
        >
          <Tabs value={tab} onChange={(event, value) => setTab(value)} variant="fullWidth" aria-label="Inspector sections" sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}>
            <Tab value="shape" label="Box" id="deck-tab-shape" aria-controls="deck-tabpanel-shape" />
            <Tab value="slide" label="Slide" id="deck-tab-slide" aria-controls="deck-tabpanel-slide" />
            <Tab value="deck" label="Deck" id="deck-tab-deck" aria-controls="deck-tabpanel-deck" />
          </Tabs>
          <Box role="tabpanel" id={`deck-tabpanel-${tab}`} aria-labelledby={`deck-tab-${tab}`} sx={{ p: 2 }}>
            {tab === 'shape' && (shape ? (
              <ShapeInspector
                key={shape.key}
                shape={shape}
                field={fieldsByShape.get(shape.key)}
                ordinal={shapeIndex + 1}
                readOnly={readOnly}
                headingRef={shapeHeadingRef}
                onAction={changeAction}
                onVariable={(variable) => apply((m) => setShapeVariable(m, shape.key, variable))}
                onFieldChange={(id, patch, coalesce) => apply((m) => updateField(m, id, patch), coalesce || null)}
                onAccept={(id) => {
                  apply((m) => acceptFields(m, [id]));
                  announce('Suggestion accepted.');
                  focusSoon(shapeHeadingRef);
                }}
                onReject={(id) => {
                  const current = fieldsByShape.get(shape.key);
                  if (current) parkedFieldsRef.current.set(shape.key, current);
                  apply((m) => rejectFields(m, [id]));
                  announce('Suggestion rejected. The box is now fixed.');
                  focusSoon(shapeHeadingRef);
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a box on the slide, or pick one from the Slide tab.
              </Typography>
            ))}
            {tab === 'slide' && slide && (
              <SlideInspector
                key={slide.key}
                slide={slide}
                shapes={slideShapes}
                fieldsByShape={fieldsByShape}
                suggestedCount={suggestionCounts[slide.key] || 0}
                readOnly={readOnly}
                headingRef={slideHeadingRef}
                onChange={(patch, coalesce) => apply((m) => updateSlide(m, slide.key, patch), coalesce || null)}
                onRestore={() => {
                  apply((m) => restoreSlide(m, slide.key));
                  announce(`Slide ${slide.number} restored.`);
                  focusSoon(slideHeadingRef);
                }}
                onDrop={() => {
                  apply((m) => dropSlide(m, slide.key));
                  announce(`Slide ${slide.number} left out.`);
                }}
                onAcceptSlide={() => {
                  const n = suggestionCounts[slide.key] || 0;
                  apply((m) => acceptFields(m, suggestedFieldIds(m, slide.key)));
                  announce(`Accepted ${n} suggestion${n === 1 ? '' : 's'} on slide ${slide.number}.`);
                  focusSoon(slideHeadingRef);
                }}
                onRejectSlide={() => {
                  const n = suggestionCounts[slide.key] || 0;
                  apply((m) => rejectFields(m, suggestedFieldIds(m, slide.key)));
                  announce(`Rejected ${n} suggestion${n === 1 ? '' : 's'} on slide ${slide.number}.`);
                  focusSoon(slideHeadingRef);
                }}
                onSelectShape={(key) => {
                  selectShape(key);
                  // The Slide tab (and the focused list item) unmounts: continue in the Box tab.
                  focusSoon(shapeHeadingRef);
                }}
              />
            )}
            {tab === 'deck' && (
              <DeckPanel
                model={model}
                counts={counts}
                slideCount={slides.length}
                droppedCount={slides.filter((s) => s.excluded).length}
                readOnly={readOnly}
                onBriefChange={(patch, coalesce) => apply((m) => updateBrief(m, patch), coalesce || null)}
                onAcceptAll={acceptAll}
              />
            )}
          </Box>
        </Box>
      </Box>
      <Box role="status" aria-live="polite" sx={VISUALLY_HIDDEN}>{announcement}</Box>
    </Box>
  );
}

export default DeckWorkspace;
